import type {ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {escapeHTML, isValidEmail} from '@Utils/string.util';
import type {AuthFormSubmissionParams, BaseAuthFormProps, ForgotPasswordParams} from '@Components/login-page/login-page.types';
import {AuthFormSubmissionType} from '@Components/login-page/login-page.types';
import {executeThunk} from '@Utils/thunk.util';
import {submitAuthForm} from '@Components/login-page/login-page-thunk';
import {EMAIL_PLACEHOLDER, redirectToPage} from '@Libraries/login-signup-library';
import type {VoidFunction} from '@Utils/general.util';
import {noop} from '@Utils/general.util';
import {Text, TextSize} from '../text';
import {AuthInput} from '../auth-input';
import {INPUT_FIELD_TYPE} from '../input-field';
import {ButtonType} from '@Components/button';
import {AuthFormSubmitButton} from '../auth-form-submit-button';
import {useAppDispatch} from '@/hooks';
import {useLoginPageForgotPasswordEmail} from '@Components/login-page/login-page.hooks';
import {updateForgotPasswordEmail} from '@Components/login-page/login-page-reducer';
import {useUpdateErrorStates} from '@/hooks/login-signup/useUpdateErrorStates';
import {AuthTestId} from '@/test-ids/auth-test-id';
import styles from './forgot-password-form.module.scss';

interface ForgotPasswordFormProps extends BaseAuthFormProps {
  onSubmitCallback?: VoidFunction;
  isLoggedInUser?: boolean;
}

export function ForgotPasswordForm({showSmallButton = false, onSubmitCallback = noop, isLoggedInUser = false}: ForgotPasswordFormProps) {
  const carriedEmail = useLoginPageForgotPasswordEmail();
  const [initialEmail] = useState(carriedEmail);
  const [email, setEmail] = useState(initialEmail);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [resetPasswordEmailSent, setResetPasswordEmailSent] = useState(false);
  const dispatch = useAppDispatch();

  /** The carried address is for this one visit; clearing it keeps it out of an unrelated forgot-password panel later. */
  useEffect(() => {
    dispatch(updateForgotPasswordEmail(''));
  }, []);

  const {showErrors} = useUpdateErrorStates();

  const handleEmailInputChange = React.useCallback((txt: string) => {
    setEmail(txt.trim());
  }, []);

  /**
   * Validity is otherwise only recomputed on submit, which leaves an error from an earlier attempt
   * latched over a corrected address. Revalidating on blur matches LoginForm.
   */
  const onEmailFocusOut = (): void => {
    if (email === '') {
      return;
    }
    setIsEmailInvalid(!isValidEmail(email));
  };

  const getTextForInformationContainer = (): string => {
    if (resetPasswordEmailSent) {
      return window.i18next.t('pmwjs_forgot_password_reset_email_sent_information', {
        email: escapeHTML(email),
      });
    }
    return window.i18next.t('pmwjs_forgot_password_email_information');
  };

  const onSubmit = async () => {
    if (!isValidEmail(email)) {
      setIsEmailInvalid(true);
      return;
    }

    setIsEmailInvalid(false);
    await onForgotPasswordSubmit();
  };

  const onSubmitClick = (): void => {
    void onSubmit();
  };

  const onForgotPasswordSubmit = async () => {
    const forgotPasswordParams: ForgotPasswordParams = {
      email,
    };

    const authFormSubmissionParams: AuthFormSubmissionParams = {
      type: isLoggedInUser ? AuthFormSubmissionType.LOGGEDINFORGOTPASSWORD : AuthFormSubmissionType.FORGOTPASSWORD,
      requestParams: forgotPasswordParams,
    };

    await executeThunk(
      () => {
        return dispatch(submitAuthForm(authFormSubmissionParams));
      },
      onForgotPasswordSubmitSuccess,
      onForgotPasswordError
    );
  };

  const onForgotPasswordSubmitSuccess = (response: any): void => {
    if (response !== null && response.redirectURL !== null) {
      redirectToPage(window.PMW.util.site_url(response.redirectURL));
      return;
    }

    setResetPasswordEmailSent(true);
    onSubmitCallback();
  };

  const onForgotPasswordError = (response: any): void => {
    if (response.payload.data.redirectUrl) {
      redirectToPage(window.PMW.util.site_url(response.payload.data.redirectUrl));
      return;
    }

    showErrors(response.payload.data.message);
    setResetPasswordEmailSent(false);
  };

  const getPasswordResetEmailSentContent = (): ReactNode => {
    return (
      <div className={styles.sentStateLayout}>
        <div className={styles.sentStateBody}>
          <Text bold className={styles.sentStateHeading} size={TextSize.MD} val={window.i18next.t('pmwjs_password_reset_email_sent')} />
          <Text dangerouslySetInnerHTML size={TextSize.MD} val={getTextForInformationContainer()} />
        </div>

        <AuthFormSubmitButton
          btnText={window.i18next.t('pmwjs_resend_email')}
          customClasses="-fullwidth -noun"
          data-testid={AuthTestId.RESET_PASSWORD_BUTTON}
          onClick={onSubmitClick}
          showSmallButton={showSmallButton}
          type={ButtonType.PRIMARY}
        />
      </div>
    );
  };

  const getBaseForgotPasswordForm = (): ReactNode => {
    return (
      <div className={styles.formLayout}>
        <AuthInput
          enableFocusStyles
          errorText={window.i18next.t('pmwjs_valid_email_address')}
          handleInput={handleEmailInputChange}
          label={window.i18next.t('pmwjs_email')}
          onFocusOutCallback={onEmailFocusOut}
          onInputSubmit={onSubmitClick}
          placeholder={EMAIL_PLACEHOLDER}
          showError={isEmailInvalid}
          type={INPUT_FIELD_TYPE.EMAIL}
          value={initialEmail}
        />

        <AuthFormSubmitButton
          btnText={window.i18next.t('pmwjs_send_reset_link')}
          customClasses="-fullwidth -noun"
          data-testid={AuthTestId.RESET_PASSWORD_BUTTON}
          onClick={onSubmitClick}
          showSmallButton={showSmallButton}
          type={ButtonType.PRIMARY}
        />
      </div>
    );
  };

  return <>{resetPasswordEmailSent ? getPasswordResetEmailSentContent() : getBaseForgotPasswordForm()}</>;
}
